import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CloseIcon, ThermometerIcon } from './icons';
import {
  LegendDot,
  PlacementDetailsAction,
  PlacementDetailsActions,
  PlacementDetailsBody,
  PlacementDetailsClose,
  PlacementDetailsHeader,
  PlacementDetailsModal,
  PlacementSections,
  PlacementStatusLabel,
  PlacementStatusRow,
  PlacementTemperature,
  PlacementTitle,
} from './styles';
import { FloorplanPlacement } from '../types';
import { PLACEMENT_STATUS_COLORS } from '../const';
import { usePlacementDetails } from '../hooks/use-placement-details.hook';
import { FavoriteStarButton } from 'components/favorite-star-button';
import { useToggleResourceFavoriteMutation } from 'data/mutations/useToggleResourceFavoriteMutation.hook';
import { useGetResourceEnvironmentQuery } from 'store/resource-environment/api';

export interface PlacementFavoriteProps {
  /** The favorites flag: without it the star is not drawn at all. */
  isFavoritesEnabled?: boolean;
  /** The favorites are known; until then the star is disabled — it would read "not a favorite" for everything. */
  isFavoritesLoaded?: boolean;
  /** The user's favorites; the star is filled when the placement is in it. */
  favoriteResourceUuids?: ReadonlySet<string>;
}

interface PlacementDetailsProps extends PlacementFavoriteProps {
  placement: FloorplanPlacement;
  onClose: VoidFunction;
}

interface PlacementDetailsContentProps extends PlacementDetailsProps {
  isCompact?: boolean;
}

// The drawer shows the rounded temperature only — no band word or color; the
// full environmental panel deliberately lives on the resource page (BUILD-1177).
// Absent entirely for sensor-less desks: available:false, an error, or a missing
// temperature reading all render nothing.
const PlacementEnvironmentLine = ({
  placement,
  isCompact,
}: {
  placement: FloorplanPlacement;
  isCompact?: boolean;
}): JSX.Element | null => {
  const { currentData: environment, isError } = useGetResourceEnvironmentQuery({
    resourceUuid: placement.resource.uuid,
  });

  // isError with currentData still set = a refetch failed after an earlier success;
  // don't show a possibly-stale reading — the server already degrades real outages
  // to available:false, so an HTTP error here means something harsher.
  if (isError || !environment?.available || !environment.temperature) {
    return null;
  }

  return (
    <PlacementTemperature data-testid="placement-details-temperature" isCompact={isCompact}>
      <ThermometerIcon />
      {`${environment.temperature.display_value}°C`}
    </PlacementTemperature>
  );
};

export const PlacementDetailsContent = ({
  placement,
  onClose,
  isCompact,
  isFavoritesEnabled = false,
  isFavoritesLoaded = false,
  favoriteResourceUuids,
}: PlacementDetailsContentProps): JSX.Element => {
  const intl = useIntl();
  const { mutate: toggleFavorite, isPending, variables } = useToggleResourceFavoriteMutation();
  const resourceUuid = placement.resource.uuid;
  // Until the toggle's refetch lands the star shows the state it asked for, so it answers the tap.
  const isFavorite =
    isPending && variables?.resourceUuid === resourceUuid
      ? variables.nextIsFavorite
      : !!favoriteResourceUuids?.has(resourceUuid);
  // One toggle at a time: a second request racing the first could land in either order and leave
  // the opposite state saved.
  const isFavoriteDisabled = isPending || !isFavoritesLoaded;
  const onFavoriteClick = useCallback(() => {
    if (isFavoriteDisabled) return;
    toggleFavorite({ resourceUuid, nextIsFavorite: !isFavorite });
  }, [toggleFavorite, resourceUuid, isFavorite, isFavoriteDisabled]);
  const { status, statusLabelId, sectionNames, heldBy, openResource } = usePlacementDetails(placement);
  const colors = PLACEMENT_STATUS_COLORS[status];

  return (
    <PlacementDetailsBody data-testid="placement-details-body" isCompact={isCompact}>
      <PlacementDetailsHeader>
        <PlacementTitle data-testid="placement-details-name" isCompact={isCompact}>
          {placement.resource.name}
        </PlacementTitle>
        <PlacementDetailsActions>
          {isFavoritesEnabled && (
            <FavoriteStarButton isFavorite={isFavorite} onClick={onFavoriteClick} disabled={isFavoriteDisabled} />
          )}
          <PlacementDetailsClose
            type="button"
            isCompact={isCompact}
            data-testid="placement-details-close"
            aria-label={intl.formatMessage({ id: 'close' })}
            onClick={onClose}
          >
            <CloseIcon />
          </PlacementDetailsClose>
        </PlacementDetailsActions>
      </PlacementDetailsHeader>
      <PlacementStatusRow>
        {/* Who holds the desk IS its status (prototype: "Booked by Vera Kral" in the status colour),
            so the holder replaces the generic label rather than adding a line under it. */}
        <PlacementStatusLabel color={colors.stroke} data-testid="placement-details-status" isCompact={isCompact}>
          <LegendDot fill={colors.fill} stroke={colors.stroke} />
          {heldBy
            ? intl.formatMessage({ id: heldBy.messageId }, heldBy.values)
            : intl.formatMessage({ id: statusLabelId })}
        </PlacementStatusLabel>
        {!!sectionNames.length && (
          <PlacementSections data-testid="placement-details-sections" isCompact={isCompact}>
            {sectionNames.join(' · ')}
          </PlacementSections>
        )}
      </PlacementStatusRow>
      <PlacementEnvironmentLine placement={placement} isCompact={isCompact} />
      <PlacementDetailsAction
        data-testid="placement-details-view"
        isCompact={isCompact}
        variant="Primary"
        text={intl.formatMessage({ id: 'viewDetails' })}
        onClick={openResource}
      />
    </PlacementDetailsBody>
  );
};

export const PlacementDetails = ({
  placement,
  onClose,
  isFavoritesEnabled,
  isFavoritesLoaded,
  favoriteResourceUuids,
}: PlacementDetailsProps): JSX.Element => (
  <PlacementDetailsModal
    onClose={onClose}
    content={
      <PlacementDetailsContent
        placement={placement}
        onClose={onClose}
        isFavoritesEnabled={isFavoritesEnabled}
        isFavoritesLoaded={isFavoritesLoaded}
        favoriteResourceUuids={favoriteResourceUuids}
      />
    }
  />
);
